import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, Stack } from 'omaha-design-system';
import {
  CUSTOM_STAT_OPTIONS,
  CUSTOM_STAT_CATEGORIES,
  MAX_CUSTOM_COLUMNS,
  DEFAULT_CUSTOM_KEYS,
} from './statsCalculations';

interface NbaRatesCustomColumnPickerProps {
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
  onClose: () => void;
}

const labelMap = new Map(CUSTOM_STAT_OPTIONS.map((o) => [o.key, o.label]));

const NbaRatesCustomColumnPicker: React.FC<NbaRatesCustomColumnPickerProps> = ({
  selectedKeys,
  onChange,
  onClose,
}) => {
  const selectedSet = new Set(selectedKeys);
  const atLimit = selectedKeys.length >= MAX_CUSTOM_COLUMNS;

  // ── Drag state ────────────────────────────────────────────────────────
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const dragNode = useRef<HTMLElement | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      setDragIndex(index);
      dragNode.current = e.target as HTMLElement;
      e.dataTransfer.effectAllowed = 'move';
      // Make the drag image slightly transparent
      requestAnimationFrame(() => {
        if (dragNode.current) dragNode.current.style.opacity = '0.4';
      });
    },
    [],
  );

  const handleDragEnd = useCallback(() => {
    if (dragNode.current) dragNode.current.style.opacity = '1';
    setDragIndex(null);
    setDropIndex(null);
    dragNode.current = null;
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragIndex !== null && index !== dragIndex) {
        setDropIndex(index);
      }
    },
    [dragIndex],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, toIndex: number) => {
      e.preventDefault();
      if (dragIndex === null || dragIndex === toIndex) return;
      const reordered = [...selectedKeys];
      const [moved] = reordered.splice(dragIndex, 1);
      reordered.splice(toIndex, 0, moved);
      onChange(reordered);
      setDragIndex(null);
      setDropIndex(null);
    },
    [dragIndex, selectedKeys, onChange],
  );

  // ── Move via arrow buttons ────────────────────────────────────────────
  const handleMove = useCallback(
    (index: number, direction: -1 | 1) => {
      const target = index + direction;
      if (target < 0 || target >= selectedKeys.length) return;
      const reordered = [...selectedKeys];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      onChange(reordered);
    },
    [selectedKeys, onChange],
  );

  const handleToggle = (key: string) => {
    if (selectedSet.has(key)) {
      onChange(selectedKeys.filter((k) => k !== key));
    } else if (!atLimit) {
      onChange([...selectedKeys, key]);
    }
  };

  const handleRemove = (key: string) => {
    onChange(selectedKeys.filter((k) => k !== key));
  };

  const handleReset = () => {
    onChange([...DEFAULT_CUSTOM_KEYS]);
  };

  const handleClear = () => {
    onChange([]);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pt: '5vh',
        bgcolor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <Box
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{
          bgcolor: '#0D0F13',
          border: '1px solid #1E2330',
          borderRadius: '12px',
          width: '100%',
          maxWidth: 640,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: '1rem 1.25rem',
            borderBottom: '1px solid #1E2330',
            background: 'linear-gradient(135deg, #1A1D24 0%, #14171C 100%)',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack spacing={0.25}>
            <Typography
              sx={{ fontSize: '1rem', fontWeight: 700, color: '#E8EAED' }}
            >
              Custom Columns
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
              Select up to {MAX_CUSTOM_COLUMNS} stats — drag to reorder
            </Typography>
          </Stack>

          {/* Counter */}
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              bgcolor: atLimit ? 'rgba(239, 68, 68, 0.15)' : 'rgba(59, 130, 246, 0.15)',
              border: `1px solid ${atLimit ? 'rgba(239, 68, 68, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: atLimit ? '#EF4444' : '#3B82F6',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {selectedKeys.length} / {MAX_CUSTOM_COLUMNS}
            </Typography>
          </Box>
        </Box>

        {/* ── Column Order Strip ──────────────────────────────────────── */}
        {selectedKeys.length > 0 && (
          <Box
            sx={{
              p: '0.625rem 1.25rem',
              borderBottom: '1px solid #1E2330',
              bgcolor: '#111318',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                mb: 0.5,
              }}
            >
              Column Order (drag to reorder)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {selectedKeys.map((key, index) => {
                const isBeingDragged = dragIndex === index;
                const isDropTarget = dropIndex === index;

                return (
                  <Box
                    key={key}
                    draggable
                    onDragStart={(e: React.DragEvent) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e: React.DragEvent) => handleDragOver(e, index)}
                    onDrop={(e: React.DragEvent) => handleDrop(e, index)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      pl: '6px',
                      pr: '2px',
                      py: '3px',
                      borderRadius: '6px',
                      bgcolor: isDropTarget
                        ? 'rgba(59, 130, 246, 0.25)'
                        : 'rgba(59, 130, 246, 0.1)',
                      border: `1px solid ${
                        isDropTarget
                          ? '#3B82F6'
                          : isBeingDragged
                            ? 'rgba(59, 130, 246, 0.3)'
                            : 'rgba(59, 130, 246, 0.2)'
                      }`,
                      cursor: 'grab',
                      userSelect: 'none',
                      transition: 'border-color 0.1s ease, background 0.1s ease',
                      '&:active': { cursor: 'grabbing' },
                    }}
                  >
                    {/* Position number */}
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        color: '#3B82F6',
                        minWidth: 14,
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {index + 1}
                    </Typography>

                    {/* Label */}
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#E8EAED',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {labelMap.get(key) || key}
                    </Typography>

                    {/* Arrow buttons */}
                    <Box
                      component="button"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleMove(index, -1);
                      }}
                      sx={{
                        width: 16,
                        height: 16,
                        p: 0,
                        border: 'none',
                        bgcolor: 'transparent',
                        color: index === 0 ? '#2A2F3A' : '#6B7280',
                        cursor: index === 0 ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '3px',
                        flexShrink: 0,
                        '&:hover': index === 0 ? {} : { color: '#E8EAED' },
                      }}
                    >
                      <Box component="svg" viewBox="0 0 12 12" sx={{ width: 10, height: 10 }}>
                        <path d="M8 10L4 6l4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </Box>
                    </Box>
                    <Box
                      component="button"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleMove(index, 1);
                      }}
                      sx={{
                        width: 16,
                        height: 16,
                        p: 0,
                        border: 'none',
                        bgcolor: 'transparent',
                        color: index === selectedKeys.length - 1 ? '#2A2F3A' : '#6B7280',
                        cursor: index === selectedKeys.length - 1 ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '3px',
                        flexShrink: 0,
                        '&:hover': index === selectedKeys.length - 1 ? {} : { color: '#E8EAED' },
                      }}
                    >
                      <Box component="svg" viewBox="0 0 12 12" sx={{ width: 10, height: 10 }}>
                        <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                      </Box>
                    </Box>

                    {/* Remove button */}
                    <Box
                      component="button"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleRemove(key);
                      }}
                      sx={{
                        width: 16,
                        height: 16,
                        p: 0,
                        border: 'none',
                        bgcolor: 'transparent',
                        color: '#6B7280',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '3px',
                        flexShrink: 0,
                        '&:hover': { color: '#EF4444' },
                      }}
                    >
                      <Box component="svg" viewBox="0 0 12 12" sx={{ width: 10, height: 10 }}>
                        <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ── Stat Categories ────────────────────────────────────────── */}
        <Box sx={{ overflowY: 'auto', flex: 1, p: '0.75rem 1.25rem' }}>
          {CUSTOM_STAT_CATEGORIES.map((category) => {
            const options = CUSTOM_STAT_OPTIONS.filter(
              (o) => o.category === category,
            );
            if (options.length === 0) return null;

            return (
              <Box key={category} sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#3B82F6',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    mb: 0.75,
                  }}
                >
                  {category}
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: '6px',
                  }}
                >
                  {options.map((option) => {
                    const isSelected = selectedSet.has(option.key);
                    const isDisabled = !isSelected && atLimit;

                    return (
                      <Box
                        key={option.key}
                        component="button"
                        onClick={() => !isDisabled && handleToggle(option.key)}
                        title={option.description}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          px: 1,
                          py: 0.625,
                          borderRadius: '6px',
                          border: `1px solid ${isSelected ? 'rgba(59, 130, 246, 0.5)' : '#1E2330'}`,
                          bgcolor: isSelected
                            ? 'rgba(59, 130, 246, 0.1)'
                            : 'transparent',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.4 : 1,
                          transition: 'all 0.15s ease',
                          '&:hover': isDisabled
                            ? {}
                            : {
                                bgcolor: isSelected
                                  ? 'rgba(59, 130, 246, 0.15)'
                                  : '#111318',
                                borderColor: isSelected
                                  ? 'rgba(59, 130, 246, 0.6)'
                                  : '#2A2F3A',
                              },
                        }}
                      >
                        {/* Checkbox indicator */}
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '4px',
                            border: `2px solid ${isSelected ? '#3B82F6' : '#4B5563'}`,
                            bgcolor: isSelected ? '#3B82F6' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {isSelected && (
                            <Box
                              component="svg"
                              viewBox="0 0 12 12"
                              sx={{ width: 10, height: 10 }}
                            >
                              <path
                                d="M2 6l3 3 5-5"
                                stroke="white"
                                strokeWidth="2"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </Box>
                          )}
                        </Box>

                        <Typography
                          sx={{
                            fontSize: '0.8rem',
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected ? '#E8EAED' : '#9CA3AF',
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: '0.75rem 1.25rem',
            borderTop: '1px solid #1E2330',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1}>
            <Box
              component="button"
              onClick={handleReset}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#6B7280',
                bgcolor: 'transparent',
                border: '1px solid #1E2330',
                borderRadius: '6px',
                cursor: 'pointer',
                '&:hover': { color: '#E8EAED', borderColor: '#2A2F3A' },
              }}
            >
              Reset
            </Box>
            <Box
              component="button"
              onClick={handleClear}
              sx={{
                px: 1.5,
                py: 0.5,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#6B7280',
                bgcolor: 'transparent',
                border: '1px solid #1E2330',
                borderRadius: '6px',
                cursor: 'pointer',
                '&:hover': { color: '#E8EAED', borderColor: '#2A2F3A' },
              }}
            >
              Clear All
            </Box>
          </Stack>

          <Box
            component="button"
            onClick={onClose}
            sx={{
              px: 2.5,
              py: 0.625,
              fontSize: '0.85rem',
              fontWeight: 700,
              color: 'white',
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'opacity 0.15s ease',
              '&:hover': { opacity: 0.9 },
            }}
          >
            Done
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NbaRatesCustomColumnPicker;
